import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, TypographyP, cn } from "@finster/ui";
import { CircleCheckBig, Flag, Library, ListTodo, Loader2, Presentation } from "lucide-react";
import { ChatHistory } from "./ChatHistory";
import { ChatHistoryDropdownMenu } from "./ChatHistoryDropdownMenu";
import { ImageInsertPanel } from "./ImageInsertPanel";
import { SupportFeedbackDialog } from "./SupportFeedbackDialog";
import { SlideCitationsView } from "./SlideCitationsView";
import { ThoughtProcessContent } from "./ThoughtProcessContent";
import { AgentTimeline } from "./AgentActivity/AgentTimeline";
import { SimpleMarkdown } from "./SimpleMarkdown";
import { useOutputFeedbackHydration } from "../../shared/components/OutputFeedback";
import {
  hasAgentTimelineActivity,
  selectLiveReplyPreview,
  visibleAgentHops,
} from "../services/ai/agentStream";
import type { AgentTimelineState } from "../services/ai/agentStream";
import {
  ThoughtProcessPanel,
  resolveLiveThoughtProcessStatus,
} from "../../shared/components/thoughtProcess";
import QueryBar, {
  type QueryBarHandle,
  type QueryBarSelection,
} from "../../shared/components/QueryBar";
import { enhancePrompt } from "../../shared/api/finster";
import {
  ComposerAttachmentPicker,
  type ComposerAttachmentMenuAction,
} from "../../shared/components/ComposerAttachmentPicker";
import { ComposerAttachmentChipRow } from "../../shared/components/ComposerAttachmentChipRow";
import {
  ComposerUploadBoundary,
  ComposerUploadChips,
} from "../../shared/components/composerUploads";
import { SelectedUserFileChips } from "../../shared/components/SelectedUserFileChips";
import { createSkillsPickerMenuAction } from "../../shared/components/skillsPickerMenuAction";
import { useDataSourcesPickerMenuAction } from "../../shared/components/useDataSourcesPickerMenuAction";
import { GettingStartedSkills } from "../../shared/components/GettingStartedSkills";
import { JumpToLatestButton } from "../../shared/components/JumpToLatestButton";
import {
  findEnabledSkillByPath,
  getSkillPromptInsertion,
  parseResolvedSkillTokensFromPrompt,
  parseSelectedSkillsFromPrompt,
  MatchingSkillBannerSlot,
  SkillSlashSuggestions,
  SkillsView,
  useSkillSlashController,
  useSkillsController,
  MAX_SELECTED_SKILLS,
  type SkillSummary,
} from "../../shared/skills";
import type { MatchingSkillBannerController } from "../../shared/skills/hooks/useMatchingSkillBanner";
import type { SelectedSkillReference, SkillDecisionSkill } from "../../shared/skills";
import { SelectedTaskContextChip } from "./SelectedTaskContextChip";
import { selectDataSources, selectUserFiles } from "../../shared/researchSelection";
import { Tab, type ChatTaskContext } from "../types";
import {
  closeCitationDialog,
  openCitationDialog,
  type Citation,
  type SourceItem,
} from "../citations";
import { QueryResult, useActiveBrand, useStickToBottom } from "../hooks";
import { ExtractedImage } from "../services/pdfService";
import { isTaskGeneratedMessage, sanitizeUserFacingText, stripVisionLabel } from "../utils";
import { BRANDS, type BrandId } from "../services/ai/brandRegistry";
import type { SkillSubmissionOptions } from "../services/ai/skills";
import { getAvailableFinsterQueryDataSources } from "../finsterApi";
import type { FeatureFlags, FinsterQueryDataSource } from "../finsterApi";
import { isAddinYourFilesUploadEnabled } from "../api/finster/constants";
import { openTaskOutputDialog } from "../services/taskOutputDialogService";
import { openSlideLibraryDialog } from "../slideLibrary/ui/dialog/dialogTransport";
import { buildAccountScopeKey } from "../../shared/auth/accountScope";
import { getPowerPointModel } from "../../shared/remoteConfig/powerPointModel";
import { ModelSettingsPicker } from "../../shared/modelPicker/ModelSettingsPicker";
import {
  readComposerDraft,
  saveComposerDraft,
} from "../../shared/clientUpdate/composerDraftStorage";

const EMPTY_COMPOSER_SELECTION: QueryBarSelection = {
  start: 0,
  end: 0,
  direction: "none",
};

interface ChatViewProps {
  cameFromReview: boolean;
  deckReviewResultsAvailable?: boolean;
  setCameFromReview: (value: boolean) => void;
  setAiMode: (mode: "chat" | "create" | "review") => void;
  taskContext: ChatTaskContext | null;
  setTaskContext: (ctx: ChatTaskContext | null) => void;
  activeTab: Tab | undefined;

  result: QueryResult | null;
  handleQuery: (
    query: string,
    dataSources?: readonly FinsterQueryDataSource[],
    userFileIds?: readonly string[],
    skillSubmission?: SkillSubmissionOptions
  ) => void | boolean | Promise<void | boolean>;
  onCancelQuery?: () => void;
  matchingSkillBanner?: MatchingSkillBannerController;
  onReplaceSkillRun?: (input: {
    prompt: string;
    selectedSkills: readonly SelectedSkillReference[];
    dataSources?: readonly FinsterQueryDataSource[];
    userFileIds?: readonly string[];
  }) => Promise<boolean>;
  isDeckEmpty?: boolean;
  pdfExtractedImages: ExtractedImage[];
  log: (message: string) => void;
  sortedTabs?: readonly Tab[];
  onSelectTab?: (tabId: string) => void;
  onViewAllChats?: () => void;
  brandStyle: BrandId | null;
  setBrandStyle: (value: BrandId | null) => void;
  plannerMode?: boolean;
  setPlannerMode?: (value: boolean) => void;
  featureFlags?: FeatureFlags;
  featureFlagsLoading?: boolean;
  userEmail?: string;
  userCompanyId?: string;
  appEnv?: string;
  /**
   * Your Files owned by the chat this view is showing. Supplying the pair makes
   * the selection session-owned, which is what keeps a Skill's files — and the
   * user's own attachments — inside the conversation that chose them. Without
   * the pair the view keeps an internal selection for the lifetime of the mount.
   */
  selectedUserFileIds?: readonly string[];
  onUserFileIdsChange?: (fileIds: string[]) => void;
  skillsStateKey?: string;
  /** Account/deck/chat identity, independent of slide-driven Skills resets. */
  uploadStateKey?: string;
  skillsViewOpen?: boolean;
  skillsComposerFocusKey?: number;
  manageSkillsUrl?: string;
  onOpenSkills?: () => void;
  onCloseSkills?: () => void;
  onTrySkillInChat?: () => void;
}

const NO_USER_FILE_IDS: readonly string[] = [];
const noopSelectTab = (_tabId: string) => undefined;
const noopViewAllChats = () => undefined;

const THINKING_PHASES = new Set([
  "Thinking...",
  "Reading slide content...",
  "Capturing slide image...",
]);

const StreamingThinking: React.FC<{
  thinking: string;
  loadingStatus: string;
  agentTimeline?: AgentTimelineState;
  collapseSignal?: number;
}> = ({ thinking, loadingStatus, agentTimeline, collapseSignal }) => {
  const [open, setOpen] = React.useState(true);
  const lastCollapseSignalRef = React.useRef<number | undefined>(undefined);
  const raw = stripVisionLabel(sanitizeUserFacingText(thinking));
  const hasTimeline = Boolean(agentTimeline) && hasAgentTimelineActivity(agentTimeline);
  const visibleHopCount = visibleAgentHops(agentTimeline).length;
  const replyPreview = selectLiveReplyPreview(agentTimeline);

  React.useEffect(() => {
    if (collapseSignal === undefined || collapseSignal === lastCollapseSignalRef.current) return;
    lastCollapseSignalRef.current = collapseSignal;
    setOpen(false);
  }, [collapseSignal]);

  const liveStatus = resolveLiveThoughtProcessStatus({
    isLive: true,
    visibleHopCount,
    hasReasoning: Boolean(raw),
    loadingStatus,
    thinkingPhases: THINKING_PHASES,
  });

  return (
    <ThoughtProcessPanel open={open} onOpenChange={setOpen} liveStatus={liveStatus}>
      {hasTimeline && agentTimeline ? (
        <>
          <AgentTimeline timeline={agentTimeline} streaming />
          {replyPreview && (
            <div className="mt-3 rounded-lg border border-border/60 bg-background px-3 py-2 text-[12px] leading-relaxed text-foreground/80">
              <SimpleMarkdown text={replyPreview} compact />
            </div>
          )}
        </>
      ) : raw ? (
        <ThoughtProcessContent text={raw} />
      ) : null}
    </ThoughtProcessPanel>
  );
};

export const ChatView: React.FC<ChatViewProps> = ({
  cameFromReview,
  deckReviewResultsAvailable = false,
  setCameFromReview,
  setAiMode,
  taskContext,
  setTaskContext,
  activeTab,
  result,
  handleQuery,
  onCancelQuery,
  matchingSkillBanner,
  onReplaceSkillRun,
  isDeckEmpty = false,
  pdfExtractedImages,
  log,
  sortedTabs = [],
  onSelectTab,
  onViewAllChats,
  brandStyle,
  setBrandStyle,
  plannerMode = false,
  setPlannerMode,
  featureFlags,
  featureFlagsLoading = false,
  userEmail,
  userCompanyId,
  appEnv,
  selectedUserFileIds: chatUserFileIds,
  onUserFileIdsChange,
  skillsStateKey,
  uploadStateKey,
  skillsViewOpen = false,
  skillsComposerFocusKey,
  manageSkillsUrl,
  onOpenSkills,
  onCloseSkills,
  onTrySkillInChat,
}) => {
  const activeBrand = useActiveBrand(userCompanyId) ?? BRANDS.default;
  const brandStyleOn = brandStyle === activeBrand.id;
  const queryBarRef = React.useRef<QueryBarHandle>(null);
  const [composerValue, setComposerValue] = React.useState(() =>
    readComposerDraft("powerpoint", skillsStateKey ?? "")
  );
  const [composerSelection, setComposerSelection] = React.useState<QueryBarSelection>({
    start: 0,
    end: 0,
    direction: "none",
  });
  /**
   * Identity whose Skill state has finished resetting. Passive effects run
   * after a render is committed, so between the render that first sees a new
   * `skillsStateKey` and the reset below, everything the controller holds still
   * belongs to the chat, deck or account the user just left. Masking it for
   * that render — rather than only clearing it afterwards — keeps the previous
   * context's attachments out of the next one's first painted frame, and out of
   * the session the write-back effect would have put them in.
   */
  const [settledSkillsStateKey, setSettledSkillsStateKey] =
    React.useState(skillsStateKey);
  const skillsResetPending = settledSkillsStateKey !== skillsStateKey;
  React.useEffect(() => {
    if (settledSkillsStateKey === skillsStateKey) return;
    setComposerValue(readComposerDraft("powerpoint", skillsStateKey ?? ""));
    setComposerSelection(EMPTY_COMPOSER_SELECTION);
    setSettledSkillsStateKey(skillsStateKey);
  }, [settledSkillsStateKey, skillsStateKey]);
  const composerDraft = skillsResetPending ? "" : composerValue;

  // Toggle between the chat history view and the aggregated citations
  // view (Figma frame 3068:24642). The toggle is per-slide-tab so
  // switching slides resets back to the chat view — see the effect
  // below that watches `activeTab?.id`.
  const [chatView, setChatView] = React.useState<"chat" | "citations">("chat");
  React.useEffect(() => {
    setChatView("chat");
  }, [activeTab?.id]);

  // Keep the chat pinned to the bottom as the thought-process / agent timeline
  // streams in, while respecting manual scroll-up. `scrollToBottom` is used to
  // force-snap when a new turn begins. Messages are in deps so tab switches and
  // transcript updates re-run the hook while pinned (same rationale as Excel).
  // `followStreamedContent` matches Excel: one scroll container serves chat and
  // citations, and resuming follow must resync pin/button state from layout
  // without a manual scroll.
  const {
    scrollContainerRef: scrollRef,
    scrollToBottom,
    showReturnToBottom,
  } = useStickToBottom<HTMLDivElement>(
    [
      activeTab?.streamingThinking,
      activeTab?.agentTimeline,
      activeTab?.loadingStatus,
      activeTab?.session.messages,
    ],
    { followStreamedContent: chatView === "chat" }
  );
  const dataSourcesInitializedRef = React.useRef(false);
  const [supportDialogOpen, setSupportDialogOpen] = React.useState(false);
  useOutputFeedbackHydration({ sessionId: activeTab?.session.id });

  // Citations for the in-flight turn (resolves inline `<u>` clicks in the
  // streaming "Thought process" pane).
  const lastAssistantCitations: Citation[] = React.useMemo(() => {
    if (isDeckEmpty) return [];
    const msgs = activeTab?.session.messages ?? [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const msg = msgs[i];
      if (msg.role === "assistant" && Array.isArray(msg.citations) && msg.citations.length > 0) {
        return msg.citations as Citation[];
      }
    }
    return [];
  }, [activeTab?.session.messages, isDeckEmpty]);

  const openCitationSource = React.useCallback((citations: Citation[], id: string) => {
    void openCitationDialog({ citationId: id, citations });
  }, []);
  const openSourceDialog = React.useCallback((citations: Citation[], item: SourceItem) => {
    void openCitationDialog({ sourceItem: item, citations });
  }, []);
  React.useEffect(() => {
    closeCitationDialog();
  }, [activeTab?.id, isDeckEmpty]);
  React.useEffect(() => {
    if (isDeckEmpty && chatView !== "chat") {
      setChatView("chat");
    }
  }, [isDeckEmpty, chatView]);

  const availableDataSources = useMemo(
    () => getAvailableFinsterQueryDataSources(featureFlags, featureFlagsLoading),
    [featureFlags, featureFlagsLoading]
  );
  const [selectedDataSources, setSelectedDataSources] =
    React.useState<FinsterQueryDataSource[]>(availableDataSources);
  // Used only when no owner was supplied, so a standalone view still has a
  // working picker. When the chat owns the selection this is never read.
  const [ownFileIds, setOwnFileIds] = useState<string[]>([]);
  const selectedUserFileIds = onUserFileIdsChange
    ? (chatUserFileIds ?? NO_USER_FILE_IDS)
    : ownFileIds;
  const taskOutputAccountScopeKey = buildAccountScopeKey({
    userEmail,
    companyId: userCompanyId,
  });
  const [taskOutputDialogPending, setTaskOutputDialogPending] = useState(false);
  const taskOutputDialogPromiseRef = React.useRef<Promise<ChatTaskContext | null> | null>(null);
  const taskOutputDialogGenerationRef = React.useRef(0);
  const taskOutputAccountScopeRef = React.useRef(taskOutputAccountScopeKey);
  taskOutputAccountScopeRef.current = taskOutputAccountScopeKey;
  const handleUserFileIdsChange = useCallback(
    (fileIds: string[]) => {
      const selection = selectUserFiles(selectedDataSources, fileIds);
      setSelectedDataSources(selection.dataSources);
      if (onUserFileIdsChange) onUserFileIdsChange(selection.userFileIds);
      else setOwnFileIds(selection.userFileIds);
    },
    [onUserFileIdsChange, selectedDataSources]
  );
  const handleDataSourcesChange = useCallback(
    (dataSources: FinsterQueryDataSource[]) => {
      // Files and ordinary sources are independent selections: picking sources
      // leaves the attached files exactly as they are.
      setSelectedDataSources(selectDataSources(selectedUserFileIds, dataSources).dataSources);
    },
    [selectedUserFileIds]
  );
  const skillsController = useSkillsController({
    resetKey: skillsStateKey,
    visibleUserFileIds: selectedUserFileIds,
    browseOpen: skillsViewOpen,
  });
  const replaceComposerRange = React.useCallback<QueryBarHandle["replaceRange"]>(
    (replacement, start, end, options) => {
      queryBarRef.current?.replaceRange(replacement, start, end, options);
    },
    []
  );
  const focusComposer = React.useCallback<QueryBarHandle["focus"]>((options) => {
    queryBarRef.current?.focus(options);
  }, []);
  const resolvedPromptSkillTokens = React.useMemo(
    () => parseResolvedSkillTokensFromPrompt(composerDraft, skillsController.skills),
    [composerDraft, skillsController.skills]
  );
  const resolvedPromptSkillsKey = resolvedPromptSkillTokens
    .map(({ skill }) => `${skill.id}:${skill.slug}`)
    .join("\0");
  React.useEffect(() => {
    skillsController.replaceSelectedSkills(resolvedPromptSkillTokens.map(({ skill }) => skill));
  }, [resolvedPromptSkillsKey]);
  const insertSkillIntoPrompt = React.useCallback(
    (skill: SkillSummary): boolean => {
      const queryBar = queryBarRef.current;
      if (!queryBar || !skill.enabled) return false;
      const value = queryBar.getValue();
      const selected = parseSelectedSkillsFromPrompt(value, skillsController.skills);
      if (
        selected.length >= MAX_SELECTED_SKILLS ||
        selected.some(
          (selectedSkill) =>
            selectedSkill.id === skill.id || selectedSkill.slug === skill.slug
        )
      ) {
        return false;
      }
      const selection = queryBar.getSelection();
      const insertion = getSkillPromptInsertion(
        value,
        selection.start,
        selection.end,
        skill.slug
      );
      queryBar.replaceRange(insertion.replacement, insertion.start, insertion.end, {
        focus: true,
        selectionMode: "end",
      });
      return true;
    },
    [skillsController.skills]
  );
  const handleSelectSuggestedSkill = React.useCallback(
    (path: string) => {
      const skill = findEnabledSkillByPath(skillsController.skills, path);
      if (skill) insertSkillIntoPrompt(skill);
    },
    [insertSkillIntoPrompt, skillsController.skills]
  );
  const skillSlashController = useSkillSlashController({
    value: composerDraft,
    selection: skillsResetPending ? EMPTY_COMPOSER_SELECTION : composerSelection,
    skills: skillsController.skills,
    selectedSkills: skillsController.selectedSkills,
    replaceRange: replaceComposerRange,
    focus: focusComposer,
    disabled: !isDeckEmpty && Boolean(activeTab?.isLoading),
  });
  const lastSkillsComposerFocusKeyRef = React.useRef(skillsComposerFocusKey);
  React.useEffect(() => {
    if (
      skillsComposerFocusKey === undefined ||
      skillsComposerFocusKey === lastSkillsComposerFocusKeyRef.current
    ) {
      return;
    }
    lastSkillsComposerFocusKeyRef.current = skillsComposerFocusKey;
    window.setTimeout(() => queryBarRef.current?.focus(), 0);
  }, [skillsComposerFocusKey]);

  React.useEffect(() => {
    // The controller's selection is still the previous chat's until its own
    // reset lands. Writing it back now would store one conversation's
    // attachments as another's.
    if (skillsResetPending) return;
    // Only files this chat owns are written back. A Skill's files are
    // prompt-scoped until the request that used them is accepted, at which
    // point the composer promotes them into this manual selection — so what
    // lands in the session is always what belongs to this conversation.
    const manualFileIds = skillsController.promptFileSelection.manualFileIds;
    const unchanged =
      manualFileIds.length === selectedUserFileIds.length &&
      manualFileIds.every((fileId, index) => fileId === selectedUserFileIds[index]);
    if (!unchanged) {
      handleUserFileIdsChange([...manualFileIds]);
    }
  }, [
    handleUserFileIdsChange,
    selectedUserFileIds,
    skillsResetPending,
    skillsController.promptFileSelection.manualFileIds,
  ]);

  React.useEffect(() => {
    taskOutputDialogGenerationRef.current += 1;
    taskOutputDialogPromiseRef.current = null;
    setTaskOutputDialogPending(false);

    return () => {
      taskOutputDialogGenerationRef.current += 1;
      taskOutputDialogPromiseRef.current = null;
    };
  }, [taskOutputAccountScopeKey]);

  const effectiveUserFileIds = skillsResetPending
    ? selectedUserFileIds
    : skillsController.effectiveUserFileIds;
  const handleVisibleUserFileIdsChange = (fileIds: string[]) => {
    skillsController.replaceVisibleUserFileIds(fileIds);
  };
  const handleAttachTaskOutput = useCallback(() => {
    if (!userEmail || taskOutputDialogPromiseRef.current) return;

    const requestGeneration = taskOutputDialogGenerationRef.current;
    const requestAccountScopeKey = taskOutputAccountScopeKey;
    let dialogPromise: Promise<ChatTaskContext | null>;
    try {
      dialogPromise = openTaskOutputDialog({ userEmail });
    } catch {
      return;
    }

    taskOutputDialogPromiseRef.current = dialogPromise;
    setTaskOutputDialogPending(true);
    return dialogPromise
      .then((selection) => {
        const requestIsCurrent =
          taskOutputDialogPromiseRef.current === dialogPromise &&
          taskOutputDialogGenerationRef.current === requestGeneration &&
          taskOutputAccountScopeRef.current === requestAccountScopeKey;
        if (requestIsCurrent && selection) {
          setTaskContext(selection);
        }
      })
      .catch(() => {
        // Keep the current attachment if the popup fails unexpectedly.
      })
      .finally(() => {
        if (
          taskOutputDialogPromiseRef.current !== dialogPromise ||
          taskOutputDialogGenerationRef.current !== requestGeneration ||
          taskOutputAccountScopeRef.current !== requestAccountScopeKey
        ) {
          return;
        }
        taskOutputDialogPromiseRef.current = null;
        setTaskOutputDialogPending(false);
      });
  }, [setTaskContext, taskOutputAccountScopeKey, userEmail]);
  const visibleMessages = React.useMemo(
    () =>
      isDeckEmpty
        ? []
        : (activeTab?.session.messages ?? []).filter((message) => !isTaskGeneratedMessage(message)),
    [activeTab?.session.messages, isDeckEmpty]
  );
  const hasReviewResults = cameFromReview && deckReviewResultsAvailable;

  const isEmptyState =
    (isDeckEmpty || (!activeTab?.isLoading && !result)) && visibleMessages.length === 0;
  const lastUserMessage = visibleMessages
    .slice()
    .reverse()
    .find((message) => message.role === "user");
  const lastAssistantMessage = visibleMessages
    .slice()
    .reverse()
    .find((message) => message.role === "assistant");

  // Force-snap when a turn *starts*, so the user's own message is in view.
  // Rising edge only: snapping on both edges of `isLoading` would snap again at
  // completion and yank someone who scrolled up mid-turn. Turn completion goes
  // through the pin check inside `useStickToBottom` instead.
  const wasLoadingRef = React.useRef(false);
  const loadingTabIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const isLoading = activeTab?.isLoading ?? false;
    const activeTabId = activeTab?.id ?? null;
    if (activeTabId !== loadingTabIdRef.current) {
      wasLoadingRef.current = false;
    }
    if (isLoading && !wasLoadingRef.current) scrollToBottom();
    wasLoadingRef.current = isLoading;
    loadingTabIdRef.current = activeTabId;
  }, [activeTab?.id, activeTab?.isLoading, scrollToBottom]);

  /**
   * One scroll container serves every slide tab. Switching tabs swaps the
   * transcript but leaves `scrollTop` and hook pin state from the prior tab
   * until reset — mirror Excel's workbook-key snap so follow mode and the
   * return-to-bottom affordance recover on idle tab switches.
   */
  React.useEffect(() => {
    if (!activeTab?.id) return;
    scrollToBottom();
  }, [activeTab?.id, scrollToBottom]);

  React.useEffect(() => {
    if (featureFlagsLoading) {
      return;
    }

    setSelectedDataSources((current) => {
      if (!dataSourcesInitializedRef.current) {
        dataSourcesInitializedRef.current = true;
        return availableDataSources;
      }

      const availableSet = new Set(availableDataSources);
      const filtered = current.filter((source) => availableSet.has(source));
      return filtered.length === current.length ? current : filtered;
    });
  }, [availableDataSources, featureFlagsLoading]);

  const handleComposerSubmit = async (query: string) => {
    const submittedSkills = parseSelectedSkillsFromPrompt(query, skillsController.skills);
    // Taken before the host is asked, so the files are the ones this request
    // carried rather than whatever the composer holds once it answers.
    const submittedSkillFiles = skillsController.captureSubmittedSkillFiles(
      submittedSkills.map((skill) => skill.id)
    );
    const result = await Promise.resolve(
      handleQuery(query, selectedDataSources, effectiveUserFileIds, {
        selectedSkills: submittedSkills,
        // An empty prompt can mean two different things, and a
        // continuation inherits its frozen Skill only for one of them: the
        // user never touched the picker. Removing every chip is a choice,
        // so the flag comes from the controller rather than the chips that
        // are left.
        selectionProvided: submittedSkills.length > 0 || skillsController.selectionProvided,
      })
    );

    // `handleQuery` resolves at accepted handoff, not when the run finishes.
    const accepted = result !== false;
    // Only reset when QueryBar is about to clear this same draft. A later
    // reset would abort hydration for a /slug typed while this Promise waited.
    const liveDraft = queryBarRef.current?.getValue() ?? query;
    if (accepted && liveDraft.trim() === query.trim()) {
      // The Skill chip is gone after this send, but the files it attached stay
      // with the chat that used them, so a follow-up here still reaches them.
      // Order matters: the reset below keeps the manual selection, which is
      // what these files have just become.
      skillsController.retainAcceptedSkillFiles(submittedSkillFiles);
      skillsController.resetSelectedSkills();
    }
    // Reported back so the prompt text settles on the same answer as the Skill
    // selection: a refused request leaves the composer exactly as the user left it.
    return accepted;
  };

  const dataSourcesMenuAction = useDataSourcesPickerMenuAction({
    selectedDataSources,
    onDataSourcesChange: handleDataSourcesChange,
    featureFlags,
    featureFlagsLoading,
  });

  const handleBannerViewSkill = (skill: SkillDecisionSkill) => {
    skillsController.openDetail(skill);
    onOpenSkills?.();
  };

  const handleReplaceSkillRun = (input: {
    prompt: string;
    selectedSkills: SelectedSkillReference[];
  }) => {
    if (!onReplaceSkillRun) return Promise.resolve();
    return onReplaceSkillRun({
      ...input,
      dataSources: selectedDataSources,
      userFileIds: effectiveUserFileIds,
    });
  };

  const renderAttachmentPicker = (disabled: boolean) => {
    const defaultsMenuAction: ComposerAttachmentMenuAction | undefined = activeBrand
      ? {
          id: "defaults",
          label: "Defaults",
          description: "Use your organisation's templates",
          icon: <Presentation aria-hidden />,
          placement: "top",
          active: brandStyleOn,
          closeOnSelect: false,
          onSelect: () => {
            setBrandStyle(brandStyleOn ? null : activeBrand.id);
          },
        }
      : undefined;

    const menuActions: ComposerAttachmentMenuAction[] = [
      {
        id: "plan-mode",
        label: "Plan",
        description: "Refine instructions and choose a template",
        icon: <ListTodo aria-hidden />,
        placement: "top",
        active: plannerMode,
        closeOnSelect: false,
        onSelect: () => {
          setPlannerMode?.(!plannerMode);
        },
      },
      ...(defaultsMenuAction ? [defaultsMenuAction] : []),
      dataSourcesMenuAction,
      createSkillsPickerMenuAction({
        controller: skillsController,
        onSelectSkill: insertSkillIntoPrompt,
        onExploreSkills: () => {
          onOpenSkills?.();
        },
      }),
      {
        id: "slide-library",
        label: "Slide Library",
        icon: <Library aria-hidden />,
        managesFocus: true,
        placement: "middle",
        onSelect: () => {
          try {
            openSlideLibraryDialog({ companyId: activeBrand?.companyId });
          } catch (err) {
            log(`Failed to open slide library: ${err instanceof Error ? err.message : String(err)}`);
          }
        },
      },
      {
        id: "attach-task-output",
        label: "Attach Task Output",
        icon: <CircleCheckBig aria-hidden />,
        placement: "middle",
        onSelect: handleAttachTaskOutput,
        disabled: disabled || taskOutputDialogPending || !userEmail,
      },
    ];

    return (
      <ComposerAttachmentPicker
        selectedFileIds={effectiveUserFileIds}
        onSelectedFileIdsChange={handleVisibleUserFileIdsChange}
        disabled={disabled || chatView !== "chat"}
        uploadEnabled={isAddinYourFilesUploadEnabled(featureFlags, featureFlagsLoading)}
        menuActions={menuActions}
      />
    );
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex-none px-4 pt-3 pb-2 flex items-center justify-between">
        <div className="flex min-w-0 items-center gap-2">
          {!isDeckEmpty && (
            <TypographyP>
              Slide{" "}
              {activeTab
                ? (activeTab.session.slideNumber ?? activeTab.session.slideIndex + 1)
                : "-"}
            </TypographyP>
          )}
          {/* Chat/Citations segmented toggle — matches Figma frame
              3068:24642. Compact pill style: 10px text inside a
              `bg-accent` track; selected tab gets a white background
              with a small shadow. Keep this inline with the slide
              label so the toolbar fits in the narrow taskpane. */}
          {!isDeckEmpty && (
            <div
              role="tablist"
              aria-label="Chat or citations"
              className="inline-flex shrink-0 items-center gap-1 rounded-lg bg-accent p-0.5"
            >
              <button
                type="button"
                role="tab"
                aria-selected={chatView === "chat"}
                onClick={() => setChatView("chat")}
                className={cn(
                  "inline-flex h-5 min-w-[28px] cursor-pointer items-center justify-center rounded-md border-0 px-1.5 text-[10px] font-medium leading-[12px] transition-colors",
                  chatView === "chat"
                    ? "bg-background text-foreground shadow-sm"
                    : "bg-transparent text-foreground/80 hover:text-foreground"
                )}
              >
                Chat
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={chatView === "citations"}
                onClick={() => setChatView("citations")}
                className={cn(
                  "inline-flex h-5 min-w-[28px] cursor-pointer items-center justify-center rounded-md border-0 px-1.5 text-[10px] font-medium leading-[12px] transition-colors",
                  chatView === "citations"
                    ? "bg-background text-foreground shadow-sm"
                    : "bg-transparent text-foreground/80 hover:text-foreground"
                )}
              >
                Citations
              </button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-1">
          <ChatHistoryDropdownMenu
            sortedTabs={sortedTabs}
            activeTabId={activeTab?.id}
            onSelectTab={onSelectTab ?? noopSelectTab}
            onViewAllChats={onViewAllChats ?? noopViewAllChats}
          />
          <button
            type="button"
            onClick={() => setSupportDialogOpen(true)}
            title="Support"
            aria-label="Open support"
            className="inline-flex h-8 w-8 cursor-pointer items-center justify-center rounded-md border-0 bg-transparent text-muted-foreground hover:bg-accent hover:text-foreground focus:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus focus-visible:ring-offset-2"
          >
            <Flag className="pointer-events-none h-4 w-4" aria-hidden />
          </button>
        </div>
      </div>

      <SupportFeedbackDialog
        open={supportDialogOpen}
        onOpenChange={setSupportDialogOpen}
        userEmail={userEmail}
        slideNumber={
          !isDeckEmpty && activeTab
            ? (activeTab.session.slideNumber ?? activeTab.session.slideIndex + 1)
            : undefined
        }
        appEnv={appEnv}
        appName="PowerPoint Add-in"
        sessionId={activeTab?.session.id}
        lastQuery={lastUserMessage?.content}
        lastResponse={lastAssistantMessage?.content}
      />

      {hasReviewResults && (
        <div className="flex-none px-4 pb-1 flex items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setCameFromReview(false);
              setAiMode("review");
            }}
            className="text-xs text-primary"
          >
            ← Back to Review
          </Button>
        </div>
      )}

      <main className="flex min-h-0 flex-1 flex-col px-4 pt-2 pb-3 overflow-hidden">
        <div
          ref={scrollRef}
          className={cn(
            "flex min-h-0 flex-1 flex-col",
            // Chat history owns the outer scroll region. Citations
            // gets an inner scroll container so grouped source cards
            // scroll inside the available taskpane height.
            chatView === "citations"
              ? "overflow-hidden"
              : "factset-scrollbar overflow-y-auto pr-1 [scrollbar-gutter:stable]"
          )}
        >
          {chatView === "chat" && (
            <>
              {visibleMessages.length > 0 && (
                <ChatHistory
                  messages={visibleMessages}
                  isLoading={activeTab.isLoading}
                  sessionId={activeTab.session.id}
                  onCitationClick={openCitationSource}
                  onSourceView={openSourceDialog}
                />
              )}

              {!isDeckEmpty && activeTab?.isLoading && (
                <StreamingThinking
                  thinking={activeTab.streamingThinking}
                  loadingStatus={activeTab.loadingStatus}
                  agentTimeline={activeTab.agentTimeline}
                  collapseSignal={activeTab.thoughtProcessCollapseSignal}
                />
              )}

              {/* Per-message apply-status (success card or failure
                  bubble) is rendered inside `ChatHistory` for each
                  assistant turn. No pinned "latest result" panel — it
                  would only duplicate the last history row. */}

              {isEmptyState && (
                // A fixed inset here used to clip the centered copy
                // whenever a header banner (e.g. an expanded
                // BackgroundDeckCreationBanner) compressed the
                // chat-history area. `GettingStartedSkills` now spaces
                // itself against the height this column gets, so there
                // is no inset to outgrow, and the parent still allows
                // vertical scroll as a final safety net.
                <div className="flex min-h-0 flex-1 flex-col">
                  <GettingStartedSkills
                    host="powerpoint"
                    manageSkillsUrl={manageSkillsUrl}
                    availableSkillSlugs={skillsController.skills.map((skill) => skill.slug)}
                    selectedSkillSlugs={skillsController.selectedSkills.map((skill) => skill.slug)}
                    selectionAtLimit={skillsController.selectionAtLimit}
                    onSelectSuggestedSkill={handleSelectSuggestedSkill}
                  />
                </div>
              )}
            </>
          )}

          {chatView === "citations" && !isDeckEmpty && (
            <div className="min-h-0 flex-1 overflow-y-auto pr-1">
              <SlideCitationsView
                messages={activeTab?.session.messages ?? []}
                onSourceView={openSourceDialog}
                onCitationView={openCitationSource}
              />
            </div>
          )}

          {/* Nothing to jump to until the chat has a turn in it. */}
          {chatView === "chat" && visibleMessages.length > 0 && (
            <JumpToLatestButton
              visible={showReturnToBottom}
              onClick={() => scrollToBottom({ behavior: "smooth" })}
            />
          )}
        </div>

        {pdfExtractedImages.length > 0 && (
          <div>
            <ImageInsertPanel images={pdfExtractedImages} onLog={log} />
          </div>
        )}

        <ComposerUploadBoundary key={uploadStateKey ?? activeTab?.session.id}>
          <div hidden={chatView !== "chat"} className="min-w-0 flex-none pt-4">
            <MatchingSkillBannerSlot
              banner={matchingSkillBanner}
              onViewSkill={handleBannerViewSkill}
              onReplaceSkillRun={onReplaceSkillRun ? handleReplaceSkillRun : undefined}
            />
            <QueryBar
              ref={queryBarRef}
              value={composerDraft}
              onValueChange={(value) => {
                setComposerValue(value);
                saveComposerDraft("powerpoint", skillsStateKey ?? "", value);
              }}
              onSelectionChange={setComposerSelection}
              onSubmit={handleComposerSubmit}
              onCancel={onCancelQuery}
              onEnhancePrompt={enhancePrompt}
              submitDisabled={skillsController.fileHydrationBlocking}
              submitDisabledReason={
                skillsController.skillFileHydrations.some(
                  (hydration) => hydration.status === "error"
                )
                  ? "Retry or remove the Skill whose attached files could not be prepared."
                  : "Preparing attached Skill files."
              }
              isLoading={!isDeckEmpty && (activeTab?.isLoading || false)}
              renderTrailingControls={(disabled) => (
                <ModelSettingsPicker
                  host="powerpoint"
                  getDefaultSelection={() => ({
                    model: getPowerPointModel(),
                    effort: "low",
                  })}
                  defaultLabel="PowerPoint default"
                  disabled={disabled}
                />
              )}
              renderAboveInput={(disabled) => (
                <div className="flex min-w-0 flex-col gap-2">
                  <ComposerAttachmentChipRow>
                    <SelectedTaskContextChip
                      name={taskContext?.name ?? null}
                      onClear={() => setTaskContext(null)}
                      disabled={disabled}
                    />
                    <SelectedUserFileChips
                      selectedFileIds={effectiveUserFileIds}
                      fileDescriptionsById={
                        skillsResetPending
                          ? undefined
                          : skillsController.fileDescriptionsByFileId
                      }
                      onRemoveFileId={(fileId) => {
                        skillsController.removeUserFileId(fileId);
                      }}
                      disabled={disabled}
                    />
                    <ComposerUploadChips disabled={disabled} />
                  </ComposerAttachmentChipRow>
                  {!skillsResetPending && skillsController.fileSelectionError && (
                    <div
                      role="alert"
                      className="rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive"
                    >
                      {skillsController.fileSelectionError.message}
                    </div>
                  )}
                  {!skillsResetPending &&
                    skillsController.skillFileHydrations.map((hydration) => {
                      if (hydration.status === "loading") {
                        return (
                          <div
                            key={hydration.skill.id}
                            role="status"
                            className="flex items-center gap-2 rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground"
                          >
                            <Loader2 className="size-3.5 animate-spin" aria-hidden />
                            Preparing attached files from /{hydration.skill.slug}…
                          </div>
                        );
                      }
                      if (hydration.status === "error") {
                        return (
                          <div
                            key={hydration.skill.id}
                            role="alert"
                            className="flex flex-wrap items-center gap-2 rounded-md border border-destructive/40 bg-destructive/5 px-2.5 py-2 text-xs text-destructive"
                          >
                            <span className="min-w-0 flex-1">
                              Files from /{hydration.skill.slug}:{" "}
                              {hydration.error?.message ?? "Attached files could not be prepared."}
                            </span>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                skillsController.retrySkillFileHydration(hydration.skill.id)
                              }
                              disabled={disabled}
                              className="h-7 px-2 text-xs"
                            >
                              Retry
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => skillsController.removeSelectedSkill(hydration.skill)}
                              disabled={disabled}
                              className="h-7 px-2 text-xs"
                            >
                              Remove Skill
                            </Button>
                          </div>
                        );
                      }
                      if (hydration.skippedFileCount > 0) {
                        return (
                          <div
                            key={hydration.skill.id}
                            role="status"
                            className="rounded-md bg-muted/50 px-2.5 py-2 text-xs text-muted-foreground"
                          >
                            Some attached files from /{hydration.skill.slug} are unavailable or not
                            ready and were skipped.
                          </div>
                        );
                      }
                      return null;
                    })}
                </div>
              )}
              renderLeadingControls={renderAttachmentPicker}
              inputAriaProps={skillSlashController.inputAriaProps}
              onInputKeyDown={skillSlashController.onKeyDown}
              highlightRanges={resolvedPromptSkillTokens.map(({ start, end }) => ({
                start,
                end,
              }))}
              renderInputOverlay={({
                overlayAnchorRef,
              }: {
                overlayAnchorRef: React.RefObject<HTMLDivElement | null>;
              }) => (
                <SkillSlashSuggestions
                  open={skillSlashController.open}
                  atLimit={skillSlashController.atLimit}
                  skills={skillSlashController.suggestions}
                  activeIndex={skillSlashController.activeIndex}
                  listboxId={skillSlashController.listboxId}
                  anchorRef={overlayAnchorRef}
                  onActiveIndexChange={skillSlashController.onActiveIndexChange}
                  onSelect={skillSlashController.selectSkill}
                  onDismiss={skillSlashController.dismiss}
                />
              )}
            />
          </div>
        </ComposerUploadBoundary>
      </main>
      <SkillsView
        open={skillsViewOpen}
        controller={skillsController}
        manageSkillsUrl={manageSkillsUrl}
        onBack={() => {
          skillsController.closeDetail();
          onCloseSkills?.();
        }}
        onClose={() => {
          onCloseSkills?.();
        }}
        onTryInChat={(skill) => {
          if (!insertSkillIntoPrompt(skill)) return;
          onTrySkillInChat?.();
        }}
      />
    </div>
  );
};
